import React from 'react';
import { Link } from 'react-router-dom';
import ArrowUpRight from 'lucide-react/dist/esm/icons/arrow-up-right';
import ArrowLeftRight from 'lucide-react/dist/esm/icons/arrow-left-right';
import Landmark from 'lucide-react/dist/esm/icons/landmark';
import Coins from 'lucide-react/dist/esm/icons/coins';
import CreditCard from 'lucide-react/dist/esm/icons/credit-card';
import SEOHead from './SEOHead';
import SITE_META from './public/siteMeta.json';
import PublicShell, { APP_STORE_URL, BUSINESS_URL, PLAY_STORE_URL } from './public/PublicShell';
import StoreButtons from './public/StoreButton';
import PartnerWall from './public/PartnerWall';
import PhoneDeck from './public/PhoneDeck';
import Timeline from './public/Timeline';
import { detectPlatform, STORES } from './public/platform';
import { CurrencyDiscs, LiveEyebrow, Reveal, ScrubText, SplitLines } from './public/primitives';
import { BODY, EYEBROW, H1, H2, INTRO, LINK_QUIET, MONO, SECTION, WRAP } from './public/register';

// Title, description and share card come from siteMeta.json, which the build also stamps
// into the static HTML that link unfurlers read.
const META = SITE_META.pages['/'];

/* ---------------------------------------------------------------------------
   yunlen.app home — mobile first, in the Yunlen Business register.

   Written for a 390px column and widened from there: a left-aligned hero with
   the store badges above the fold, one phone beneath it (three fanned out on
   desktop), then lists and hairlines rather than a grid of tiles, and one
   statement lit word by word on scroll. A phone sees only its own store
   button, and a get-the-app bar rises from the bottom edge once the hero has
   scrolled away.
   --------------------------------------------------------------------------- */

const STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Yunlen',
  description:
    'Yunlen is a phone app for cross-border transfers, USD and EUR virtual accounts, stablecoin trading, and digital cards.',
  url: SITE_META.origin,
  logo: `${SITE_META.origin}/icon-512.png`,
  sameAs: [PLAY_STORE_URL, APP_STORE_URL],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@yunlen.app',
    url: SITE_META.origin,
  },
  offers: {
    '@type': 'Offer',
    name: 'Yunlen',
    description: 'Transfers, USD and EUR virtual accounts, stablecoin trading, and digital cards',
    category: 'Financial Services',
  },
  applicationCategory: 'FinanceApplication',
  operatingSystem: ['Android', 'iOS'],
};

const HERO_LINES = ['Transfers, accounts,', 'cards, stablecoins.', 'In one app.'];
const HERO_CODES = ['USD', 'EUR', 'GBP', 'USDC', 'USDT'];

const PRODUCTS = [
  {
    Icon: ArrowLeftRight,
    title: 'Transfers',
    sub: 'Cross-border transfers. The rate is shown before you send, and each transfer updates as it moves.',
    chip: 'Rate shown before you send',
  },
  {
    Icon: Landmark,
    title: 'Virtual accounts',
    sub: 'USD and EUR virtual accounts, for personal use and for a business.',
    chip: 'USD and EUR',
  },
  {
    Icon: Coins,
    title: 'Stablecoins',
    sub: 'Buy and sell stablecoins, including USDB. The rate is in the app, and settlement is immediate.',
    chip: 'Including USDB',
  },
  {
    Icon: CreditCard,
    title: 'Cards',
    sub: 'Digital cards for online and in-person purchases. You top them up from the app.',
    chip: 'Top up in the app',
  },
];

const STEPS = [
  { title: 'Download', desc: 'Get Yunlen from the App Store or Google Play and create your account in the app.' },
  { title: 'Verify', desc: 'Confirm who you are with an ID, in the app. Account approval finishes within minutes.' },
  { title: 'Move money', desc: 'Open USD and EUR accounts, send a transfer, buy stablecoins, or issue a card.' },
];

const STATEMENT =
  'Send money across borders, hold dollars and euros next to stablecoins, and pay with a card. One login, one balance, from your phone.';
const STATEMENT_ACCENT = ['one', 'phone'];

/** On a phone the bar goes straight to that phone's store; elsewhere to the download page. */
function stickyCta() {
  const platform = detectPlatform();
  return platform ? { label: 'Get the app', href: STORES[platform].web } : { label: 'Download', to: '/download' };
}

const HomePage = () => (
  <>
    <SEOHead
        title={META.title}
        description={META.description}
        keywords={META.keywords}
        canonicalUrl={`${SITE_META.origin}/`}
        ogImage={`${SITE_META.origin}${META.ogImage}`}
        ogImageWidth={1200}
        ogImageHeight={630}
        structuredData={STRUCTURED_DATA}
      />
    <PublicShell cta={stickyCta()}>
      {/* ============================== HERO ============================== */}
      <section className="relative overflow-clip pt-8 sm:pt-14 lg:pt-16">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[40rem] overflow-hidden">
          <span className="yl-wash left-[-20%] top-[-10rem] h-[24rem] w-[28rem] bg-[#cfeee0] lg:left-[4%] lg:w-[34rem]" />
          <span className="yl-wash yl-wash--b right-[-20%] top-[6rem] h-[20rem] w-[24rem] bg-[#9fd9bf]/60 lg:right-[6%] lg:w-[30rem]" />
          <span className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-b from-transparent to-[#ffffff]" />
        </div>

        <div className={`${WRAP} grid items-center gap-6 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,0.75fr)] lg:gap-4`}>
          <div className="flex flex-col items-start">
            <LiveEyebrow>The personal app</LiveEyebrow>

            {/* Three lines at every width: the desktop size follows the viewport so the widest line never wraps. */}
            <h1 aria-label={HERO_LINES.join(' ')} className={`${H1} mt-5 sm:mt-7 lg:!text-[clamp(3.25rem,4.4vw,4.25rem)]`}>
              <SplitLines lines={HERO_LINES} delay={0.1} accentLast className="block" />
            </h1>

            <p className={`${INTRO} mt-5 max-w-[34rem] sm:mt-7`}>
              Cross-border transfers, USD and EUR virtual accounts, stablecoin trading, and digital cards. Account data is encrypted.
            </p>

            <StoreButtons className="mt-7 sm:mt-9" />

            <div className="mt-6 flex items-center gap-3">
              <CurrencyDiscs codes={HERO_CODES} size={36} enter />
              <span className={`${MONO} text-[0.625rem] uppercase leading-snug tracking-[0.12em] text-[#565656]`}>
                USD, EUR, GBP, and crypto
                <br />
                in the same app
              </span>
            </div>
          </div>

          <PhoneDeck className="-mb-4 lg:mb-0 lg:h-[38rem]" />
        </div>
      </section>

      {/* ============================ PARTNERS ============================
          The Yunlen Business partner wall: the personal app's partners, no rails. */}
      <div className="pt-12 sm:pt-16">
        <div className={WRAP}>
          <p className={`${EYEBROW} mb-7 text-[#565656] sm:text-center`}>Our partners</p>
          <PartnerWall />
        </div>
      </div>

      {/* ============================ PRODUCTS ============================
          A list, not tiles: an icon, a title, one sentence and a mono tag,
          separated by hairlines. Two columns from tablet up. */}
      <section id="products" className={`${SECTION} yl-defer pt-14 sm:pt-20 lg:pt-28`}>
        <div className={WRAP}>
          <Reveal className="flex flex-col gap-4 sm:gap-6">
            <span className={`${EYEBROW} text-[#565656]`}>What it does</span>
            <h2 className={`${H2} max-w-[16ch]`}>Four things, one balance.</h2>
          </Reveal>
          <Reveal as="ul" className="mt-10 grid border-t border-[#ececec] sm:grid-cols-2 lg:mt-16" stagger={0.06} y={16}>
            {PRODUCTS.map(({ Icon, title, sub, chip }) => (
              <li key={title} className="flex gap-4 border-b border-[#ececec] py-7 sm:gap-5 sm:py-9 sm:odd:pr-8 sm:even:border-l sm:even:pl-8 lg:odd:pr-14 lg:even:pl-14">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#f5f5f5] text-[#171717]" aria-hidden="true">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div className="min-w-0">
                  <h3 className="text-[1.375rem] font-medium leading-tight tracking-[-0.02em] text-[#171717] lg:text-[1.75rem]">{title}</h3>
                  <p className={`${BODY} mt-2 max-w-[40ch]`}>{sub}</p>
                  <p className={`${MONO} mt-4 flex items-center gap-2 text-[0.625rem] uppercase tracking-[0.1em] text-[#0f7a55] sm:text-[0.6875rem]`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-[#10b981]" aria-hidden="true" />
                    {chip}
                  </p>
                </div>
              </li>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ============================== STEPS ============================== */}
      <section className={`${SECTION} yl-defer pt-0 sm:pt-0 lg:pt-0`}>
        <div className={WRAP}>
          <Reveal className="flex flex-col gap-4 sm:gap-6">
            <span className={`${EYEBROW} text-[#565656]`}>Getting started</span>
            <h2 className={`${H2} max-w-[16ch]`}>From download to first transfer.</h2>
          </Reveal>
          <Reveal className="mt-10 lg:mt-16" y={16}>
            <Timeline steps={STEPS} />
          </Reveal>
        </div>
      </section>

      {/* ============================ STATEMENT ============================
          One paragraph, lit word by word as it scrolls through (GSAP scrub). */}
      <section className={`${SECTION} yl-defer pt-0 sm:pt-0 lg:pt-0`}>
        <div className={WRAP}>
          <ScrubText
            text={STATEMENT}
            accent={STATEMENT_ACCENT}
            className="max-w-[24ch] text-[2rem] font-medium leading-[1.12] tracking-[-0.03em] text-[#171717] sm:text-[3rem] lg:max-w-[28ch] lg:text-[4rem]"
          />
        </div>
      </section>

      {/* ========================= BUSINESS + CTA ========================= */}
      <section className={`${SECTION} yl-defer pt-0 sm:pt-0 lg:pt-0`}>
        <div className={WRAP}>
          <a href={BUSINESS_URL} className="group flex items-center justify-between gap-5 border-y border-[#ececec] py-7 sm:py-9">
            <span className="flex min-w-0 flex-col gap-2">
              <span className={`${EYEBROW} text-[#565656]`}>Running a company?</span>
              <span className="text-[1.25rem] font-medium leading-snug tracking-[-0.02em] text-[#171717] sm:text-[1.75rem] lg:text-[2.25rem]">
                Yunlen Business adds staff cards, bulk payouts, and roles.
              </span>
            </span>
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#f5f5f5] text-[#171717] transition-colors duration-300 group-hover:bg-[#171717] group-hover:text-[#ffffff]" aria-hidden="true">
              <ArrowUpRight className="h-5 w-5" />
            </span>
          </a>

          <Reveal className="relative mt-20 flex flex-col items-start gap-7 sm:items-center sm:text-center lg:mt-28">
            <span aria-hidden="true" className="pointer-events-none absolute -bottom-24 left-1/2 h-[18rem] w-[18rem] -translate-x-1/2 rounded-full bg-[#10b981]/15 blur-3xl" />
            <h2 className={`${H2} relative max-w-[14ch]`}>Get Yunlen on your phone.</h2>
            <StoreButtons className="relative sm:justify-center" />
            <Link to="/download" className={`${LINK_QUIET} relative`}>
              Open the download page
            </Link>
          </Reveal>
        </div>
      </section>
    </PublicShell>
  </>
);

export default HomePage;
